export const vuetifyColors = {
  primary: '#9e9e9e', // Grau
  secondary: '#4caf50', // raus
  tertiary: '#616161', // raus
  accent: '#82B1FF', // raus
  error: '#f55a4e', // raus
  warning: '#ffc400', // Yellow #FFF900 in darker mode #eae400
  success: '#41d927', // Green #46EA2A in darker mode #41d927
  info: '#00e9e9', // Blue #00FFFF in darker mode #00e9e9
  danger: '#F5144B', // Rot
  neon: '#fbc02d', // raus
  darkBlue: '#1565c0', // Dunkelblau
  niotix: '#00A9E1', // niotix brand color
};

// The allowed colors in order for the color scheme selectors
export const themeColors = ['success', 'niotix', 'darkBlue', 'primary', 'warning', 'danger'];

export const themeDefaults = {
  mode: 'light',
  useAltFonts: false,
  useCustomBackground: false,
  locale: 'de',
  pageTitle: 'IoT Platform',
  lightHighlight: 'niotix',
  lightBackground: '/img/themes/d-suite/background-light.jpg',
  lightLogo: '/img/themes/d-suite/d-suite-logo-light.svg',
  lightLogoOrientation: 'landscape',
  // NOTE: the default-logo is d-suite, which is displayed in the center,
  // BUT this is a new introduced attribute, so existing CUSTOM logos should still be placed on the left
  // this may lead to minor confuson in the UI if the logo is not changed from the default, which then is displayed in the center,
  // but otherwise a DB-migration is nececssary
  lightLogoPosition: 'left',
  lightLogoByline: 'NIOTIX',
  darkHighlight: 'niotix',
  darkBackground: '/img/themes/d-suite/background-dark.gif',
  darkLogo: '/img/themes/d-suite/d-suite-logo-dark.svg',
  darkLogoOrientation: 'landscape',
  darkLogoPosition: 'left', // so note for lightLogoPosition about default here
  darkLogoByline: 'NIOTIX',
  rowsPerPage: 20,
  favicon: '/favicon/niotix-x-144x144.png',
};

function hexToHSL(hex) {
  // Remove the # if present
  hex = hex.replace('#', '');

  // Convert hex to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return [h * 360, s * 100, l * 100];
}

function hslToHex(h, s, l) {
  h /= 360;
  s /= 100;
  l /= 100;

  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) {
        t += 1;
      }
      if (t > 1) {
        t -= 1;
      }
      if (t < 1 / 6) {
        return p + (q - p) * 6 * t;
      }
      if (t < 1 / 2) {
        return q;
      }
      if (t < 2 / 3) {
        return p + (q - p) * (2 / 3 - t) * 6;
      }
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (x) => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export const adjustColorLightness = (hex, amount) => {
  const [h, s, l] = hexToHSL(hex);
  return hslToHex(h, s, Math.max(0, l - amount));
};

export const getContrastYIQ = (hexcolor = '', dark = '#000000', light = '#ffffff', threshold = 160) => {
  const hexRgxEp = /^#?[a-f0-9]{3}([a-f0-9]{3})?$/i;

  if (!hexRgxEp.test(hexcolor)) {
    throw new Error('getContrastYIQ() expects a valid hex color string as first argument');
  }

  // remove optional leading #
  hexcolor = hexcolor.replace('#', '');

  // convert shorthand hex to full length hex
  if (hexcolor.length === 3) {
    hexcolor = hexcolor.replace(/([^#])/g, '$1$1');
  }

  const r = parseInt(hexcolor.substring(0, 2), 16);
  const g = parseInt(hexcolor.substring(2, 4), 16);
  const b = parseInt(hexcolor.substring(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= threshold ? dark : light;
};

import { mapState } from 'vuex';
export const themesMixin = {
  computed: {
    ...mapState('app', ['UiTheme', 'color', 'themes', 'colorHexLight', 'colorHexDark']),
    customColorDark() {
      return this.colorHexDark || this.colorHexLight;
    },
    customColorLight() {
      return this.colorHexLight || this.colorHexDark;
    },
  },
  methods: {
    // http://martin.ankerl.com/2009/12/09/how-to-create-random-colors-programmatically/
    randomColor(saturation = 0.6, value = 0.9) {
      const golden_ratio_conjugate = 0.618033988749895;
      const hue = ((Math.random() + golden_ratio_conjugate) % 1.0) * 6.0;
      const hueInt = Math.floor(hue);

      const f = hue - hueInt;
      const p = value * (1 - saturation);
      const q = value * (1 - f * saturation);
      const t = value * (1 - (1 - f) * saturation);

      let r, g, b;
      switch (hueInt) {
        case 0:
          // r, g, b = v, t, p if h_i==0
          r = value;
          g = t;
          b = p;
          break;
        case 1:
          // r, g, b = q, v, p if h_i==1
          r = q;
          g = value;
          b = p;
          break;
        case 2:
          // r, g, b = p, v, t if h_i==2
          r = p;
          g = value;
          b = t;
          break;
        case 3:
          // r, g, b = p, q, v if h_i==3
          r = p;
          g = q;
          b = value;
          break;
        case 4:
          // r, g, b = t, p, v if h_i==4
          r = t;
          g = p;
          b = value;
          break;
        default:
          // r, g, b = v, p, q if h_i==5
          r = value;
          g = p;
          b = q;
          break;
      }
      return { r: r * 256, g: g * 256, b: b * 256 };
    },
    rgbToHex(rgb) {
      const r = `00${(~~(rgb.r * 255)).toString(16)}`.slice(-2);
      const g = `00${(~~(rgb.g * 255)).toString(16)}`.slice(-2);
      const b = `00${(~~(rgb.b * 255)).toString(16)}`.slice(-2);
      return `#${r}${g}${b}`;
    },
    vuetifyColorHex(fallback) {
      if (vuetifyColors[this.$store.state.app.color]) {
        return vuetifyColors[this.$store.state.app.color];
      }

      const mode = this.UiTheme ? 'dark' : 'light';
      const fallbackColor = vuetifyColors[fallback] ? vuetifyColors[fallback] : vuetifyColors['primary'];

      return (mode === 'dark' ? this.customColorDark : this.customColorLight) || fallbackColor;
    },
    themeGetDefaults() {
      return themeDefaults;
    },
    isDefaultSvgLogo() {
      const mode = this.UiTheme ? 'dark' : 'light';
      const defaultLogo = mode === 'light' ? themeDefaults.lightLogo : themeDefaults.darkLogo;
      const currentLogo = ((this.themes[mode] || {}).logo || '').split('?')[0];
      // If no logo set or matches default (ignoring cache-buster), treat as default SVG
      return !currentLogo || currentLogo === defaultLogo;
    },
    logoPosition() {
      const mode = this.UiTheme ? 'dark' : 'light';
      return this.themes[mode].position;
    },
    loginContainerStyle() {
      if (this.logoPosition() === 'center-overlap' || this.isDefaultSvgLogo()) {
        return 'fill-height login-container';
      }
      return 'fill-height login-container-custom';
    },
    themeGetBgColors() {
      const mode = this.UiTheme ? 'dark' : 'light';
      return mode === 'dark' ? 'black' : 'white';
    },
    themeGetMode() {
      const local = String(this.$store.state.app.thingshiveTheme).toLocaleLowerCase();
      if (local !== 'dark' && local !== 'light') {
        return this.$store.state.app.mode || themeDefaults.mode;
      }
      return local;
    },
    themeGetBackground() {
      const mode = this.themeGetMode();
      return this.$store.state.app.themes[mode].background;
    },
    getBG() {
      if (this.UiTheme) {
        return `background-image: url(${this.loginBackground}); background-size: cover;`;
      }
      return `background-image: url(${this.loginBackground}); background-size: cover;`;
    },
    themeGetLogoObject() {
      const mode = this.themeGetMode();
      return {
        image: this.$store.state.app.themes[mode].logo,
        orientation: this.$store.state.app.themes[mode].orientation,
        byline: this.$store.state.app.themes[mode].byline,
      };
    },
  },
};
